import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { getOrgUsersAPI } from 'src/api';
import { useDebounce } from 'src/hooks';
import {
  orgDebounceDelayAtom,
  orgFilterByAtom,
  orgSearchByAtom,
  orgSortByAtom,
  orgTotalUsersAtom,
} from 'src/store';

const MAX_USERS_PER_PAGE = process.env.NODE_ENV === 'development' ? 2 : 20; // page size of 2 for dev, 20 for prod

/**
 * Organization Settings Users Page
 *
 * Hook to fetch organization users with infinite scrolling
 * Reads filterBy, sortBy, and searchBy from Jotai atoms
 * Debounces searchBy to avoid excessive API calls
 * Supports immediate search on Enter key press
 *
 * @returns infinite query object
 */
export const useGetOrgUsers = () => {
  const [sortBy] = useAtom(orgSortByAtom);
  const [filterBy] = useAtom(orgFilterByAtom);
  const [searchBy] = useAtom(orgSearchByAtom);
  const [debounceDelay] = useAtom(orgDebounceDelayAtom);
  const [, setTotalUsers] = useAtom(orgTotalUsersAtom);

  // Debounce the searchBy value to avoid excessive API calls
  const debouncedSearchBy = useDebounce(searchBy, debounceDelay);

  return useInfiniteQuery({
    queryFn: async ({ pageParam }) => {
      const data = await getOrgUsersAPI({
        sortBy,
        filterBy,
        searchBy: debouncedSearchBy,
        page: pageParam,
      });

      setTotalUsers(data.totalUsers);

      return { users: data.users, pageParam: data.totalUsers };
    },
    queryKey: ['orgUsers', sortBy, filterBy, debouncedSearchBy],
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.users.length < MAX_USERS_PER_PAGE) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });
};
